import { EFFORT_SCALE_MAX, type EffortScale } from "@heraion/shared";

import { METER_ROW_MIN_HEIGHT } from "./MeterRow";

// The effort band's MODEL (LG-63) — which values it carries, which ramp stop
// paints each one, and where the wheel has to sit to show a given value. Pure
// arithmetic, no JSX: <EffortMeter> composes it and owns the pixels.
//
// ⛔ Why the band grew: it was the ONLY surface in the app that spoke 0..4.
// Everything else has always spoken 0..10 — the canonical model
// (EFFORT_SCALE_MAX), the program editor's prescription ("RIR must be 0..10")
// and the numeric field's own error text, which says «RIR 0–10» to the athlete.
// The band answered that with five stops and a "4+" bucket, so a prescribed
// RIR 6 landed the target ghost on a segment that COMMITTED rir 4: the
// prescription was lost in the write, not just flattened in the read
// (measured 2026-09-03, Antenore's session of the same day). The wheel is the
// band finally speaking the language the app already spoke, not a wider one.

/** Every canonical value the wheel carries, easy → hard — the same direction
 *  the five-stop band read in. RPE is the display projection (10 − rir), so
 *  this one list serves both scales. */
export const WHEEL_RIR = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0] as const;

/** One tick's width. The touch floor, spent SIDEWAYS: <MeterRow> defends 44
 *  in height because on a row of neighbours you miss horizontally and land on
 *  an adjacent effort, correctable with another tap. On a row that SCROLLS
 *  that reasoning inverts — a horizontal miss is a drag, not a wrong value —
 *  so the floor has to hold on both axes and the ticks stop sharing the width.
 *  At 280dp (MeterRow's narrow case) the window is 280 − 48 − 8 = 224, i.e.
 *  exactly the five ticks the old band showed. */
export const EFFORT_TICK_WIDTH = METER_ROW_MIN_HEIGHT;

/** Which ramp stop paints a value — now ONE PER VALUE (11 stops, this list's
 *  own order), so the mapping is the index and nothing else.
 *
 *  ⚡ 2026-09-05: this replaces CONSERVA, the flat tail Antenore chose on
 *  3/09 (`rir >= 4 ? 0 : 4 - rir` — RIR 4…10 all shared the easiest stop, so
 *  no one had to relearn a colour). He reversed it himself once the ramp
 *  became thermal: with a flat tail the colour could not tell a warm-up
 *  (RIR ≥ 6) from real light work (4-5) — the very distinction the timeline
 *  started making that same day (EFFORT_APPROACH_RIR). The accepted cost is
 *  the one CONSERVA was avoiding: every colour has moved. */
export const effortRampStopOf = (rir: number): number =>
  EFFORT_SCALE_MAX - rir;

/** The number printed on a tick, in the athlete's scale. Display only — the
 *  tick still commits the canonical rir. The old "4+" and "6−" bucket labels
 *  are gone with the bucket they named. */
export const effortTickLabel = (rir: number, scale: EffortScale): string =>
  String(scale === "rpe" ? EFFORT_SCALE_MAX - rir : rir);

/** A value's position on the wheel, or null when it isn't one of the stops.
 *  ⚠️ Half-step efforts are real (effortLabel rounds to 2 decimals), and they
 *  match no tick — exactly as they matched no segment before. Returning null
 *  keeps that: a 2.5 selects nothing rather than rounding itself onto a
 *  neighbour and claiming the athlete said something they didn't. */
export const effortWheelIndexOf = (rir: number | null): number | null => {
  if (rir == null) return null;
  const idx = (WHEEL_RIR as readonly number[]).indexOf(rir);
  return idx === -1 ? null : idx;
};

/** Where the wheel has to be scrolled to put `focusRir` in view, clamped to
 *  the scrollable range.
 *
 *  `null` → the HARD END (D2, Antenore 2026-09-03). At 280dp that window is
 *  4·3·2·1·0, i.e. the old band pixel for pixel. ⛔ It is not a cosmetic
 *  default: one tap IS the completion gesture, so a resting window on the easy
 *  values would turn every ordinary set into scroll-then-tap. The hard end
 *  keeps the sets an athlete actually logs at one touch, and leaves the
 *  scrolling for the easy set that is genuinely rare. */
export const effortWheelOffset = (
  focusRir: number | null,
  bandWidth: number,
): number => {
  const max = Math.max(0, WHEEL_RIR.length * EFFORT_TICK_WIDTH - bandWidth);
  const idx = effortWheelIndexOf(focusRir);
  if (idx == null) return max;
  const centred = (idx + 0.5) * EFFORT_TICK_WIDTH - bandWidth / 2;
  return Math.min(max, Math.max(0, centred));
};
